/* eslint-disable no-bitwise */
class DeliveryEstimateTemplate extends HTMLElement {
	disconnectedCallback() {
		if (this.reqAnimationFrameId) {
			cancelAnimationFrame(this.reqAnimationFrameId);
		}
	}

	calculate() {
		this.days = [
			'Sunday',
			'Monday',
			'Tuesday',
			'Wednesday',
			'Thursday',
			'Friday',
			'Saturday',
		];

		this.cutOffDay = this.days.findIndex(
			(day) => day === this.getAttribute('cut-off-day')
		);
		this.cutOffTime = this.getAttribute('cut-off-time');
		[this.cutOffHour, this.cutOffMinute] = this.cutOffTime.split(':');

		this.minimumDeliveryTime = parseInt(
			this.getAttribute('minimum-delivery-time'),
			10
		);
		this.maximumDeliveryTime = parseInt(
			this.getAttribute('maximum-delivery-time'),
			10
		);
		this.excludedDeliveryDates = this.getAttribute(
			'excluded-delivery-dates'
		)
			.split(',')
			.map((date) => date.trim());
		this.shouldIncludeWeekendDelivery =
			this.getAttribute('include-weekend-delivery') === '';
		this.i18nTomorrow = this.getAttribute('i18n-tomorrow');

		this.calculateDeliveryEstimate();
		this.setAttribute('calculated', true);
		this.removeAttribute('hidden');
	}

	calculateDeliveryEstimate() {
		const nextCutOffDate = this.getNextCutOffDate();
		const deliverOn = this.calculateDeliverOn(nextCutOffDate);
		const shortcodes = this.replaceShortcodes(nextCutOffDate, deliverOn);


		if (shortcodes.includes('-cut_off_countdown-')) {
			this.setCountdownLabels();

			this.cutOffCountdown(nextCutOffDate);
		}

		if (shortcodes.includes('-delivery_date:max-')) {
			this.updateDeliveryText(deliverOn);
		}

	}

	cutOffCountdown(nextCutOffDate) {
		const start = window.dayjs().utc();
		const end = window.dayjs(nextCutOffDate).utc();
		const showSeconds = end.diff(start, 'hour') < 1;

		this.updateCutOffCountdown(start, end, showSeconds);

		if (showSeconds) {
			this.reqAnimationFrameId = requestAnimationFrame(
				this.cutOffCountdown.bind(this, nextCutOffDate),
				this
			);
		}
	}

	calculateDeliverOn(nextCutOffDate) {
		return this.addWeekDays(nextCutOffDate);
	}

	getNextCutOffDate() {
		let startDate = window.dayjs().utc()
		let cannotDeliver = true;
		let weekday;
		let isWeekend;

		if (this.isAfterCutOffTime(startDate)) {
			startDate = startDate.add(1, 'day');
		}

		while (cannotDeliver) {
			weekday = startDate.format('dddd'); // Monday ... Sunday
			isWeekend = weekday === 'Sunday' || weekday === 'Saturday';
			const isExcluded = this.excludedDeliveryDates.includes(startDate.format('YYYY-MM-DD'));
			if (isExcluded || (isWeekend && !this.shouldIncludeWeekendDelivery)) {
				startDate = startDate.add(1, 'day');
			} else {
				cannotDeliver = false;
			}
		}

		return startDate.hour(this.cutOffHour)
			.minute(this.cutOffMinute)
			.second(0);
	}

	replaceShortcodes(nextCutOffDate, deliverOnDate) {
		const hasShortcodesToReplace = this.textContent.match(
			/-cut_off_countdown-|-delivery_date:max-/g
		) || [];
		let shortcodesToReplace = this.textContent;

		if (hasShortcodesToReplace.includes('-cut_off_countdown-')) {
			shortcodesToReplace = shortcodesToReplace.replace(
				/-cut_off_countdown-/g,
				`<time id="cut-off-countdown" datetime="${nextCutOffDate.toISOString()}"></time>`
			);
		}

		if (hasShortcodesToReplace.includes('-delivery_date:max-')) {
			shortcodesToReplace = shortcodesToReplace.replace(
				/-delivery_date:max-/g,
				`<time id="delivery-date-max" datetime="${deliverOnDate.toISOString()}"></time>`
			);
		}

		this.innerHTML = shortcodesToReplace;
		return hasShortcodesToReplace;
	}

	setCountdownLabels() {
		window.countdown.setLabels(
			//  millisecond| second| minute| hour| day| week| month| year| decade| century| millennium
			this.getAttribute('i18n-countdown-label-singular'),
			//  milliseconds| seconds| minutes| hours| days| weeks| months| years| decades| centuries| millennia
			this.getAttribute('i18n-countdown-label-plural'),
			//  and
			this.getAttribute('i18n-countdown-label-last'),
			// ,
			this.getAttribute('i18n-countdown-label-delim'),
			//
			this.getAttribute('i18n-countdown-label-empty'),
			(n) => n.toString()
		);
	}

	updateCutOffCountdown(start, end, showSeconds) {
		const cutOff = this.querySelector('#cut-off-countdown');
		cutOff.innerHTML = this.getCountdownText(start, end, showSeconds);
	}

	getCountdownText(start, end, showSeconds) {
		const units = this.getCountdownUnits(showSeconds);
		const countdownStartDate = start.toDate();
		let countdownEndDate = end.toDate();

		// Adjust for DST if needed
		if (this.isDST(countdownEndDate)) {
			countdownEndDate = new Date(
				countdownEndDate.getTime() - 60 * 60 * 1000
			); // Subtract 1 hour (60 mins * 60 secs * 1000 ms)
		}

		const ts = window.countdown(
			countdownStartDate,
			countdownEndDate,
			units
		);

		let cutOffCountdownText = `${this.getAttribute(
			'i18n-within'
		)} <u>${ts.toString()}</u>`;
		if (ts.hours >= 24) {
			if (this.getAttribute('show-next-cut-off-time-only') === '') {
				let now = window.dayjs();
				now = now.add(1, 'day');
				now = now.set('hour', end.hour())
					.set('minute', end.minute())
					.set('second', end.second());

				cutOffCountdownText = `${this.getAttribute(
					'i18n-by'
				)} <u>${now.format('ha')}</u>`;
			} else {
				cutOffCountdownText = `${this.getAttribute(
					'i18n-by'
				)} <u>${end.format('ha ddd Do MMM')}</u>`;
			}
		}
		return cutOffCountdownText;
	}

	// eslint-disable-next-line class-methods-use-this
	isDST(d) {
		const jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
		const jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
		return Math.max(jan, jul) !== d.getTimezoneOffset();
	}

	getDeliverOnText(deliverOn) {
		let deliverOnText = deliverOn.format('ddd Do MMM');
		if (this.minimumDeliveryTime === this.maximumDeliveryTime) {
			const tomorrow = window.dayjs().add(1, 'day').endOf('day');
			deliverOnText =
				deliverOn > tomorrow ? deliverOnText : this.i18nTomorrow;
		}
		return deliverOnText;
	}

	// eslint-disable-next-line class-methods-use-this
	getCountdownUnits(showSeconds = false) {
		if (showSeconds) {
			return (
				window.countdown.HOURS |
				window.countdown.MINUTES |
				window.countdown.SECONDS
			);
		}

		return window.countdown.HOURS | window.countdown.MINUTES;
	}

	updateDeliveryText(deliverOn) {
		const deliveryMaxNode = this.querySelector('#delivery-date-max');
		deliveryMaxNode.innerHTML = `<u>${this.getDeliverOnText(
			deliverOn
		)}</u>`;
	}

	isAfterCutOffTime(date) {
		const [cutOffHour, cutOffMinute] = this.cutOffTime.split(':');

		// Convert current time to UTC
		const currentTimeUTC = window.dayjs(date).utc();

		// Create cutoff time in UTC
		let cutOffTimeUTC = window.dayjs()
			.utc()
			.hour(parseInt(cutOffHour, 10))
			.minute(parseInt(cutOffMinute, 10))
			.second(0);

		// Adjust for DST if needed
		if (this.isDST(cutOffTimeUTC.toDate())) {
			cutOffTimeUTC = cutOffTimeUTC.subtract(1, 'hour');
		}

		return currentTimeUTC >= cutOffTimeUTC;
	}

	addWeekDays(initialDate) {
		let weekDaysAdded = 0;
		let currentDay = window.dayjs(initialDate);
		let weekday;
		let isWeekend;
		while (weekDaysAdded < this.maximumDeliveryTime) {
			currentDay = currentDay.add(1, 'day');
			weekday = currentDay.format('dddd'); // Monday ... Sunday
			isWeekend = weekday === 'Sunday' || weekday === 'Saturday';

			// Check if current day is excluded
			const isExcluded = this.excludedDeliveryDates.includes(
				currentDay.format('YYYY-MM-DD')
			);

			// Only count the day if it's not excluded and either it's not a weekend or weekend delivery is allowed
			if (!isExcluded && (!isWeekend || this.shouldIncludeWeekendDelivery)) {
				weekDaysAdded += 1;
			}
		}
		return currentDay;
	}
}

customElements.define('delivery-estimate-template', DeliveryEstimateTemplate);
